import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as Marzipano from "marzipano";

export default function VirtualTour() {
  const navigate = useNavigate();

  const [rooms, setRooms] =
    useState([]);

  const [activeRoom, setActiveRoom] =
    useState(null);

  const [hotspots, setHotspots] =
    useState([]);

  const [moving, setMoving] =
    useState(false);

  const [fullScreen, setFullScreen] =
    useState(false);

  const [showMiniMap, setShowMiniMap] =
    useState(true);

  const [countDown, setCountDown] =
    useState(30);

  const screenRef =
    useRef(null);

  const viewerRef =
    useRef(null);

  const panoRef =
    useRef(null);

  const sceneRef =
    useRef(null);

  const viewRef =
    useRef(null);

  const moveTimerRef =
    useRef(null);

  const finishTimerRef =
    useRef(null);

  const rotateRef =
    useRef(null);

  const autoNextRef =
    useRef(null);

  const countRef =
    useRef(null);

  /* =========================
     LOAD ROOMS
  ========================= */
  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms =
    async () => {
      try {
        const res =
          await axios.get(
            "https://stjosephweb.ilakshyaerp.in/api/tours"
          );

        const list =
          res.data || [];

        setRooms(list);

        if (
          list.length > 0
        ) {
          setActiveRoom(
            list[0]
          );
        }
      } catch (err) {
        console.log(err);
      }
    };

  /* =========================
     LOAD HOTSPOTS
  ========================= */
  useEffect(() => {
    if (!activeRoom)
      return;

    loadHotspots(
      activeRoom.id
    );
  }, [activeRoom]);

  const loadHotspots =
    async (id) => {
      try {
        const res =
          await axios.get(
            `https://stjosephweb.ilakshyaerp.in/api/hotspots/${id}`
          );

        setHotspots(
          res.data || []
        );
      } catch {
        setHotspots([]);
      }
    };

  /* =========================
     CREATE VIEWER ONLY ONCE
  ========================= */
  useEffect(() => {
    if (!viewerRef.current)
      return;

    panoRef.current =
      new Marzipano.Viewer(
        viewerRef.current,
        {
          controls: {
            mouseViewMode: "drag",
            scrollZoom: true
          },
        }
      );

    return () => {
      clearIntervals();

      if (
        panoRef.current &&
        panoRef.current.destroy
      ) {
        panoRef.current.destroy();
      }

      panoRef.current =
        null;
    };
  }, []);

  /* =========================
     FULLSCREEN WATCH
  ========================= */
  useEffect(() => {
    const handler =
      () => {
        setFullScreen(
          !!document.fullscreenElement
        );
      };

    document.addEventListener(
      "fullscreenchange",
      handler
    );

    return () =>
      document.removeEventListener(
        "fullscreenchange",
        handler
      );
  }, []);

  /* =========================
     CREATE SCENE
  ========================= */
  useEffect(() => {
    if (
      !activeRoom ||
      !panoRef.current
    )
      return;

    createScene();

    // eslint-disable-next-line
  }, [activeRoom, hotspots]);

  const createScene =
    () => {
      clearIntervals();

      const source =
        Marzipano.ImageUrlSource.fromString(
          `https://stjosephweb.ilakshyaerp.in/uploads/virtual-image/${activeRoom.image}`
        );

      const geometry =
        new Marzipano.EquirectGeometry(
          [
            {
              width: 4000,
            },
          ]
        );

      const limiter =
        Marzipano.RectilinearView.limit.traditional(
          1400,
          (110 *
            Math.PI) /
          180
        );

      const view =
        new Marzipano.RectilinearView(
          {
            yaw: 0,
            pitch: 0,
            fov:
              Math.PI / 2,
          },
          limiter
        );

      const scene =
        panoRef.current.createScene(
          {
            source,
            geometry,
            view,
          }
        );

      scene.switchTo({
        transitionDuration: 700,
      });

      sceneRef.current =
        scene;

      viewRef.current =
        view;

      addHotspots(
        scene
      );

      startAutoRotate();
      startAutoNext();
    };

  /* =========================
     HOTSPOTS
  ========================= */
  const addHotspots = (scene) => {
  hotspots.forEach((spot) => {
    const dir =
      spot.direction || "right";

    const wrap =
      document.createElement("div");

    wrap.className =
      "relative w-[80px] h-[80px] flex items-center justify-center cursor-pointer";

    /* PULSE */
    const pulse =
      document.createElement("div");

    pulse.className =
      "absolute w-[70px] h-[70px] rounded-full bg-[#D4A017]/40 animate-ping";

    /* BUTTON */
    const btn =
      document.createElement("div");

    btn.className =
      "relative z-20 w-[60px] h-[60px] rounded-full bg-[#D4A017] hover:bg-[#0B4F9C] text-white flex items-center justify-center text-[34px] font-bold shadow-2xl transition-all duration-300 hover:scale-110";

    btn.innerHTML = "➜";

    btn.style.pointerEvents =
      "none";

    /* ROTATION */
    if (dir === "left") {
      btn.style.transform =
        "rotate(180deg)";
    }

    if (dir === "up") {
      btn.style.transform =
        "rotate(-90deg)";
    }

    if (dir === "down") {
      btn.style.transform =
        "rotate(90deg)";
    }

    wrap.appendChild(pulse);
    wrap.appendChild(btn);

    wrap.onclick = () => {
      resetAuto();

      walkToRoom(
        Number(
          spot.target_scene_id
        )
      );
    };

    const yaw =
      ((Number(
        spot.yaw
      ) -
        50) /
        50) *
      1.2;

    const pitch =
      ((50 -
        Number(
          spot.pitch
        )) /
        50) *
      0.7;

    scene
      .hotspotContainer()
      .createHotspot(wrap, {
        yaw,
        pitch,
      });
  });
};

  /* =========================
     AUTO ROTATE
  ========================= */
  const startAutoRotate =
    () => {
      rotateRef.current =
        setInterval(() => {
          if (
            viewRef.current
          ) {
            const yaw =
              viewRef.current.yaw();

            viewRef.current.setYaw(
              yaw + 0.003
            );
          }
        }, 30);
    };

  /* =========================
     AUTO NEXT ROOM
  ========================= */
  const startAutoNext =
    () => {
      setCountDown(30);

      countRef.current =
        setInterval(() => {
          setCountDown(
            (prev) =>
              prev - 1
          );
        }, 1000);

      autoNextRef.current =
        setTimeout(() => {
          goNextRoom();
        }, 30000);
    };

  const goNextRoom =
    () => {
      if (
        !rooms.length ||
        !activeRoom
      )
        return;

      const currentIndex =
        rooms.findIndex(
          (r) =>
            r.id ===
            activeRoom.id
        );

      const nextIndex =
        (currentIndex +
          1) %
        rooms.length;

      walkToRoom(
        rooms[nextIndex]
          .id
      );
    };

  /* =========================
     CLEAR TIMERS
  ========================= */
  const clearIntervals =
    () => {
      clearInterval(
        rotateRef.current
      );

      clearTimeout(
        autoNextRef.current
      );

      clearInterval(
        countRef.current
      );
    };

  const resetAuto =
    () => {
      clearIntervals();
      startAutoRotate();
      startAutoNext();
    };

  /* =========================
     WALK ROOM
  ========================= */
  const walkToRoom =
    (id) => {
      const room =
        rooms.find(
          (r) =>
            Number(
              r.id
            ) ===
            Number(id)
        );

      if (!room)
        return;

      clearTimeout(
        moveTimerRef.current
      );

      clearTimeout(
        finishTimerRef.current
      );

      setMoving(true);

      moveTimerRef.current =
        setTimeout(() => {
          setActiveRoom(
            room
          );

          finishTimerRef.current =
            setTimeout(
              () =>
                setMoving(
                  false
                ),
              700
            );
        }, 200);
    };

  /* =========================
     ZOOM
  ========================= */
  const zoomIn = () => {
    resetAuto();

    if (!viewRef.current) return;

    let current = viewRef.current.fov();
    let next = current - 0.20;

    if (next < 0.6) next = 0.6;

    viewRef.current.setFov(next);
  };

  const zoomOut = () => {
    resetAuto();

    if (!viewRef.current) return;

    let current = viewRef.current.fov();
    let next = current + 0.20;

    if (next > 2.2) next = 2.2;

    viewRef.current.setFov(next);
  };
  /* =========================
     FULLSCREEN
  ========================= */
  const toggleFullScreen =
    () => {
      const el =
        screenRef.current;

      if (
        !document.fullscreenElement
      ) {
        el.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    };

  /* =========================
     JSX
  ========================= */
 return (
  <div className="w-full h-screen flex flex-col lg:flex-row overflow-hidden bg-[#071a2f]">

    {/* SIDEBAR */}
    <div className="w-full lg:w-[290px] lg:min-w-[290px] bg-gradient-to-b from-[#072F5F] to-[#041526] text-white p-5 lg:p-6 shadow-2xl z-50 flex flex-col">

      {/* LOGO */}
      <h1 className="text-3xl font-extrabold tracking-wide mb-6">

        School Tour

      </h1>

      {/* ROOM LIST */}
      <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto scrollbar-hide pb-2">

        {rooms.map((room) => (
          <button
            key={room.id}
            onClick={() => {
              resetAuto();
              walkToRoom(room.id);
            }}
            className={`px-5 py-4 rounded-2xl text-left whitespace-nowrap transition-all duration-300 backdrop-blur-xl text-sm font-medium shrink-0 ${
              activeRoom?.id === room.id
                ? "bg-[#D4A017] text-white shadow-lg"
                : "bg-white/10 hover:bg-[#0B4F9C] text-white"
            }`}
          >

            {room.title}

          </button>
        ))}

      </div>

    </div>

    {/* SCREEN */}
    <div
      ref={screenRef}
      className="flex-1 relative overflow-hidden bg-black"
    >

      {/* CLOSE */}
      <button
        onClick={() => navigate("/")}
        className="absolute top-4 right-4 z-[100] w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-xl text-white hover:bg-red-500 transition-all duration-300 flex items-center justify-center text-xl shadow-xl"
      >

        ✕

      </button>

      {/* VIEWER */}
      <div
        ref={viewerRef}
        className="w-full h-full"
      ></div>

      {/* WALKING */}
      {moving && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[90]">

          <h2 className="text-white text-3xl md:text-5xl font-extrabold tracking-widest">

            Walking...

          </h2>

        </div>
      )}

      {/* TIMER */}
      <div className="absolute top-4 left-4 z-[90] bg-white/10 backdrop-blur-xl text-white px-5 py-3 rounded-2xl text-sm font-semibold shadow-xl">

        Next Room in {countDown}s

      </div>

      {/* QUICK ROOM */}
      {showMiniMap && (
        <div className="hidden lg:block absolute top-24 left-4 z-[90] w-[240px] bg-white/10 backdrop-blur-2xl rounded-3xl p-5 shadow-2xl text-white">

          <h3 className="text-xl font-bold mb-4">

            Quick Rooms

          </h3>

          <div className="flex flex-col gap-3">

            {rooms.map((room) => (
              <button
                key={room.id}
                onClick={() => {
                  resetAuto();
                  walkToRoom(room.id);
                }}
                className="bg-white/10 hover:bg-[#0B4F9C] rounded-2xl px-4 py-3 text-left transition-all duration-300"
              >

                {room.title}

              </button>
            ))}

          </div>

        </div>
      )}

      {/* CONTROLS */}
      <div className="absolute right-4 md:right-6 bottom-28 z-[95] flex flex-col gap-3">

        {/* MAP */}
        <button
          className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl text-white hover:bg-[#0B4F9C] transition-all duration-300 shadow-xl text-sm font-semibold"
          onClick={() =>
            setShowMiniMap(!showMiniMap)
          }
        >

          Map

        </button>

        {/* FULLSCREEN */}
        <button
          className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl text-white hover:bg-[#0B4F9C] transition-all duration-300 shadow-xl text-sm font-semibold"
          onClick={toggleFullScreen}
        >

          {fullScreen ? "Exit" : "Full"}

        </button>

        {/* ZOOM OUT */}
        <button
          className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl text-white hover:bg-[#0B4F9C] transition-all duration-300 shadow-xl text-2xl"
          onClick={zoomOut}
        >

          −

        </button>

        {/* ZOOM IN */}
        <button
          className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl text-white hover:bg-[#0B4F9C] transition-all duration-300 shadow-xl text-2xl"
          onClick={zoomIn}
        >

          +

        </button>

      </div>

      {/* THUMBNAILS */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[95] flex gap-3 bg-white/10 backdrop-blur-2xl rounded-3xl px-4 py-3 overflow-x-auto max-w-[92%] shadow-2xl scrollbar-hide">

        {rooms.map((room) => (
          <img
            key={room.id}
            src={`https://stjosephweb.ilakshyaerp.in/uploads/virtual-image/${room.image}`}
            alt="room"
            onClick={() => {
              resetAuto();
              walkToRoom(room.id);
            }}
            className={`w-[80px] md:w-[95px] h-[55px] md:h-[65px] object-cover rounded-2xl cursor-pointer transition-all duration-300 shrink-0 ${
              activeRoom?.id === room.id
                ? "border-[3px] border-[#D4A017] scale-105"
                : "opacity-80 hover:opacity-100 hover:scale-105"
            }`}
          />
        ))}

      </div>

    </div>

  </div>
);
}